/**
 * WHAT MOVED, AND WHICH WAY. D-EZ-58.
 *
 * Howard, 18 Aug 2026: *"would it be possible to show thin on brand arrows
 * connecting the driver's moves that turn? If positive Green if negative red
 * and have it go from the edge of their card to the edge of the card that is
 * there now. If they traded places, stripe the arrow arm red and green for
 * arrows ending at both points."*
 *
 * ===========================================================================
 * THIS FILE COMPUTES NO RULES. IT IS ARITHMETIC ON TWO RUNNING ORDERS.
 * ===========================================================================
 *
 * The boards already know where a position sits — `slotFor` has been turning a
 * running-order number into coordinates since the table was built. So an arrow
 * is: take the position a driver held when the turn began, take the one he
 * holds now, ask `slotFor` for both, and trim the segment between them back to
 * the edge of each card. Nothing here decides that he moved; the engine did,
 * and this only draws the consequence.
 *
 * ===========================================================================
 * A TRADE IS ONE ARM, NOT TWO
 * ===========================================================================
 *
 * When two men swap — he takes 4th from the man who falls to 5th — the naive
 * drawing is two arrows on top of each other pointing opposite ways, which
 * reads as noise and hides that it was ONE pass. So a swap collapses into a
 * single arm with a head at each end, striped in both colours: green where the
 * man who gained arrived, red where the man who lost did.
 *
 * ⚠ ONLY AN EXACT SWAP COLLAPSES. He goes 4→5 and the other 5→4: one arm. If
 * the second man went 5→6 because a third car came through, they are two
 * separate moves and get two arrows, because that is what happened.
 *
 * ===========================================================================
 * THE BOTTOM STACK IS ONE PLACE, NOT TWENTY-FOUR
 * ===========================================================================
 *
 * ⚠ THIS RULE EXISTS BECAUSE OF A SCREENSHOT. Drawn naively, a caution that
 * shuffles the pack produced thirty arms all terminating inside a fanned pile
 * whose cards overlap by nine tenths — a green thicket that said nothing. Two
 * corrections, and both of them are the board's own model rather than a cap:
 *
 *   1. A move with BOTH ends in the BOTTOM stack is not drawn. The stack is
 *      dealt as "a mass, not a list" (see `STACK_PACK` in boardModel) — the two
 *      cards physically overlap, so an arm between them would point from a card
 *      to itself. It did not fail to happen; it has nowhere to be drawn.
 *   2. A move with ONE end in the stack is drawn to the stack's MOUTH — the
 *      top card of the rail — rather than to the fanned slot. Every arrow into
 *      the pack then converges on one point and reads as a fan.
 *
 * Nothing is silently dropped: `suppressed` counts what rule 1 left out, and
 * the board prints that count beside the box.
 */

import { slotFor } from './boardModel.ts';
import { groupOf } from '../domain/types.ts';

export interface Point {
  x: number;
  y: number;
}

export type MoveKind = 'gain' | 'loss' | 'trade';

export interface MoveArrow {
  /** Stable across a turn, so React keeps the same node. */
  id: string;
  kind: MoveKind;
  /** One driver, or the two who swapped. */
  driverIds: string[];
  /** Card edge to card edge — never centre to centre. */
  from: Point;
  to: Point;
  /** The bend, as a quadratic control point. */
  control: Point;
  /** Positions, kept for the spoken line under the boards. */
  fromPosition: number;
  toPosition: number;
  /** How many places changed hands. Always positive. */
  places: number;
}

/**
 * How far the head stops short of the card. Touching the card reads as the
 * arrow being part of it; a gap reads as pointing AT it.
 */
export const CARD_GAP = 5;

/** A straight line across a whole board is a ruler; a slight bow is a racing line. */
const BOW = 0.13;
const MAX_BOW = 46;

/**
 * ⚠ SHORTER THAN THIS AND THERE IS NO ARM TO DRAW — ANOTHER SCREENSHOT BUG.
 *
 * Two cards side by side on Board A are twelve pixels apart. Take the gap at
 * each end off that and a swap between neighbours — the single most common move
 * in the game — came out as two arrowheads touching, with no visible arm and no
 * legible colour. It looked like a rendering fault, which is worse than looking
 * like nothing.
 *
 * So a move between cards that close is BRIDGED: both ends leave through the
 * top edge instead of the facing sides, and the arm arcs over the pair. Same
 * two heads, same two colours, and now there is something between them.
 */
const MIN_ARM = 36;
const BRIDGE_LIFT = 30;

/**
 * ⚠ AND EVERY MOVE ALONG A ROW IS BRIDGED, NOT JUST THE SHORT ONES.
 *
 * Howard, 19 Aug 2026: *"The green arrows seem to have one too many arrow
 * heads. There should only be one at the end of their positive move."*
 *
 * Each arrow only ever carried ONE head — the markup is `marker-end` and
 * nothing else. What he was looking at was TWO arrows lying on top of each
 * other. Board B pairs its cards ODD-left / EVEN-right, so consecutive
 * positions are not adjacent on the table: 16th to 15th ran straight across the
 * FACE of the 14th-place card, and arrived beside a second arm that was arcing
 * over the same two roofs. Two arms, two heads, one shape.
 *
 * So a move along a row now always goes over the roofs, and the arc is lifted
 * in proportion to how far it travels. Long moves ride high over short ones and
 * the whole row reads like a set of tie lines rather than a knot — nested, each
 * one obviously a single arm with a single point on it.
 */
const ROW_LIFT_BASE = 16;
const ROW_LIFT_PER_PX = 0.17;
const MAX_ROW_LIFT = 88;

/** Two slots share a row when their cards' tops line up. */
function sameRow(a: number, b: number): boolean {
  return slotFor(anchorFor(a)).y === slotFor(anchorFor(b)).y;
}

export interface Standing {
  driverId: string;
  position: number;
}

// ---------------------------------------------------------------------------
// Geometry
// ---------------------------------------------------------------------------

/** The top card of the BOTTOM rail — the one place the whole stack is drawn to. */
export const STACK_MOUTH = 20;

export const inTheStack = (position: number): boolean => groupOf(position) === 'bottom';

/**
 * The slot an arrow should actually touch. Every position in the stack answers
 * with the stack's mouth; everything else answers with itself.
 */
export function anchorFor(position: number): number {
  return inTheStack(position) ? STACK_MOUTH : position;
}

function centreOf(position: number): Point {
  const slot = slotFor(anchorFor(position));
  return { x: slot.x + slot.w / 2, y: slot.y + slot.h / 2 };
}

function halfSizeOf(position: number): Point {
  const slot = slotFor(anchorFor(position));
  return { x: slot.w / 2, y: slot.h / 2 };
}

/**
 * Where the line from `centre` towards `toward` crosses the card's own edge,
 * pushed out by `gap`.
 *
 * ⚠ THE RECTANGLE, NOT A CIRCLE. A driver card is 118×152 — a circle around it
 * would leave the head floating well clear of a card's short sides and buried
 * in its long ones. Scaling the direction by whichever of the two half-extents
 * it runs out of first is what puts the point ON the edge, wherever the other
 * card happens to be.
 */
export function edgePoint(centre: Point, half: Point, toward: Point, gap = CARD_GAP): Point {
  const dx = toward.x - centre.x;
  const dy = toward.y - centre.y;
  const length = Math.hypot(dx, dy);
  // Two cards in the same place is not a move; the caller filters those out,
  // and returning the centre is the only answer that is not a division by zero.
  if (length === 0) return { ...centre };
  const scaleX = dx === 0 ? Infinity : Math.abs(half.x / dx);
  const scaleY = dy === 0 ? Infinity : Math.abs(half.y / dy);
  const scale = Math.min(scaleX, scaleY);
  return {
    x: centre.x + dx * scale + (dx / length) * gap,
    y: centre.y + dy * scale + (dy / length) * gap,
  };
}

/** The control point of the bow, perpendicular to the arm and to its left. */
export function bowOf(from: Point, to: Point): Point {
  const dx = to.x - from.x;
  const dy = to.y - from.y;
  const length = Math.hypot(dx, dy);
  const lift = Math.min(MAX_BOW, length * BOW);
  if (length === 0) return { x: from.x, y: from.y };
  return {
    x: (from.x + to.x) / 2 + (-dy / length) * lift,
    y: (from.y + to.y) / 2 + (dx / length) * lift,
  };
}

/** The `d` of a quadratic bow, ready for an SVG path. */
export function pathOf(arrow: MoveArrow): string {
  const r = (n: number) => Math.round(n * 10) / 10;
  return `M ${r(arrow.from.x)} ${r(arrow.from.y)} Q ${r(arrow.control.x)} ${r(arrow.control.y)} ${r(arrow.to.x)} ${r(arrow.to.y)}`;
}

// ---------------------------------------------------------------------------
// The arrows
// ---------------------------------------------------------------------------

function arm(fromPosition: number, toPosition: number): Pick<MoveArrow, 'from' | 'to' | 'control'> {
  const a = centreOf(fromPosition);
  const b = centreOf(toPosition);
  const halfA = halfSizeOf(fromPosition);
  const halfB = halfSizeOf(toPosition);
  /*
    ⚠ ALONG A ROW, ALWAYS OVER THE ROOFS. Straight across would cross the face
    of every card standing between the two — see ROW_LIFT_BASE.
  */
  if (sameRow(fromPosition, toPosition) && a.x !== b.x) {
    const f = { x: a.x, y: a.y - halfA.y - CARD_GAP };
    const t = { x: b.x, y: b.y - halfB.y - CARD_GAP };
    const lift = Math.min(MAX_ROW_LIFT, ROW_LIFT_BASE + Math.abs(t.x - f.x) * ROW_LIFT_PER_PX);
    return { from: f, to: t, control: { x: (f.x + t.x) / 2, y: Math.min(f.y, t.y) - lift } };
  }

  const from = edgePoint(a, halfA, b);
  const to = edgePoint(b, halfB, a);
  if (Math.hypot(to.x - from.x, to.y - from.y) >= MIN_ARM) {
    return { from, to, control: bowOf(from, to) };
  }
  /*
    Neighbours. The bridge goes around the SHORT way — over the roofs of two
    cards standing side by side, around the flank of two stacked one above the
    other. Always arcing over the top would send a leader-to-Board-A move
    straight across the face of the leader's own card.
  */
  const dx = b.x - a.x;
  const dy = b.y - a.y;
  if (Math.abs(dx) >= Math.abs(dy)) {
    const f = { x: a.x, y: a.y - halfA.y - CARD_GAP };
    const t = { x: b.x, y: b.y - halfB.y - CARD_GAP };
    return { from: f, to: t, control: { x: (f.x + t.x) / 2, y: Math.min(f.y, t.y) - BRIDGE_LIFT } };
  }
  // Lean the arc the way the move leans, so it never crosses the other card.
  const side = dx >= 0 ? 1 : -1;
  const f = { x: a.x + side * (halfA.x + CARD_GAP), y: a.y };
  const t = { x: b.x + side * (halfB.x + CARD_GAP), y: b.y };
  return {
    from: f,
    to: t,
    control: {
      x: side > 0 ? Math.max(f.x, t.x) + BRIDGE_LIFT : Math.min(f.x, t.x) - BRIDGE_LIFT,
      y: (f.y + t.y) / 2,
    },
  };
}

/**
 * Every move worth drawing, given where the field was and where it is.
 *
 * `before` is null before the green flag and on the first turn of a race — no
 * previous order means no move, and an arrow drawn from a starting slot to
 * itself would be a lie about a car that has not turned a wheel.
 *
 * A driver who is in one order and not the other is skipped: a car that retired
 * did not move UP or DOWN the running order, it left, and the board already
 * says so by lifting the card out of its slot.
 */
export interface MoveArrows {
  arrows: MoveArrow[];
  /** Moves that happened entirely inside the BOTTOM stack — see the header. */
  suppressed: number;
}

export function moveArrows(
  now: readonly Standing[],
  before: readonly Standing[] | null,
): MoveArrows {
  if (!before || before.length === 0) return { arrows: [], suppressed: 0 };
  const was = new Map(before.map((s) => [s.driverId, s.position]));

  const moves: { driverId: string; from: number; to: number }[] = [];
  let suppressed = 0;
  for (const car of now) {
    const from = was.get(car.driverId);
    if (from === undefined || from === car.position) continue;
    // Shuffling within the pile. There is no pair of cards to draw between.
    if (inTheStack(from) && inTheStack(car.position)) {
      suppressed += 1;
      continue;
    }
    moves.push({ driverId: car.driverId, from, to: car.position });
  }

  // An exact swap: he went p→q and somebody else went q→p.
  const byFrom = new Map(moves.map((m) => [m.from, m]));
  const spokenFor = new Set<string>();
  const arrows: MoveArrow[] = [];

  for (const move of moves) {
    if (spokenFor.has(move.driverId)) continue;
    const other = byFrom.get(move.to);
    if (other && other.to === move.from && other.driverId !== move.driverId) {
      spokenFor.add(move.driverId);
      spokenFor.add(other.driverId);
      // One arm, drawn from the WORSE position to the BETTER one, so the head
      // at the end is the green one — see the header.
      const gained = move.to < move.from ? move : other;
      const lost = gained === move ? other : move;
      arrows.push({
        id: `trade-${[move.driverId, other.driverId].sort().join('-')}`,
        kind: 'trade',
        driverIds: [gained.driverId, lost.driverId],
        fromPosition: lost.to,
        toPosition: gained.to,
        places: Math.abs(move.to - move.from),
        ...arm(lost.to, gained.to),
      });
      continue;
    }
    spokenFor.add(move.driverId);
    arrows.push({
      id: `move-${move.driverId}`,
      kind: move.to < move.from ? 'gain' : 'loss',
      driverIds: [move.driverId],
      fromPosition: move.from,
      toPosition: move.to,
      places: Math.abs(move.to - move.from),
      ...arm(move.from, move.to),
    });
  }

  // Biggest moves last, so the ones worth seeing are drawn on top.
  arrows.sort((x, y) => x.places - y.places);
  return { arrows, suppressed };
}
